import { useQuery } from '@tanstack/react-query';

import { textractApi } from 'domain/textract/api';
import type { FieldMapping } from 'domain/textract/schemas';
import { queryOptions } from 'services/query';
import { textractKeys } from './keys';

/**
 * Deduplicate fields by displayName — keeps the first occurrence.
 * Moved from the former textractFieldsMappingUniqueSelector.
 */
const deduplicateFields = (fields: FieldMapping[]): FieldMapping[] =>
  Object.values(
    fields.reduce<Record<string, FieldMapping>>((acc, field) => {
      if (!acc[field.displayName]) acc[field.displayName] = field;
      return acc;
    }, {}),
  );

export const textractFieldsMappingQuery = (documentID: string) =>
  queryOptions({
    queryKey: textractKeys.fieldsMapping(documentID),
    queryFn: () => textractApi.getFieldsMapping({ path: { documentID } }),
    staleTime: 10 * 60 * 1000, // 10 minutes
  });

/**
 * Lazily fetches the field mapping options for the 2WM binding dropdown menu.
 * Data is NOT loaded until `enabled` is true (i.e., user opens the dropdown).
 * Returns deduplicated fields (by displayName) via `select`.
 */
export const useTextractFieldsMapping = (documentID: string, enabled: boolean) =>
  useQuery({
    ...textractFieldsMappingQuery(documentID),
    enabled,
    select: deduplicateFields,
  });
